import {
  PlacementGroup,
  PlacementGroupChange,
  type CreatePlacementGroupRequest,
} from "@cloudserver/contracts";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { api } from "./client.js";

const key = (projectId: string) => ["projects", projectId, "placement-groups"] as const;

/** The project's placement groups; live updates refresh them through the `projects` key. */
export const usePlacementGroups = (projectId: string) =>
  useQuery({
    queryKey: key(projectId),
    queryFn: () =>
      api(`/projects/${projectId}/placement-groups`, { schema: z.array(PlacementGroup) }),
  });

/** Writes also change what server pages show, so every project query is refreshed. */
function useGroupMutation<T, R>(fn: (input: T) => Promise<R>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: fn,
    onSuccess: () => client.invalidateQueries({ queryKey: ["projects"] }),
  });
}

const base = (projectId: string) => `/projects/${projectId}/placement-groups`;

export const useCreatePlacementGroup = (projectId: string) =>
  useGroupMutation((body: CreatePlacementGroupRequest) =>
    api(base(projectId), {
      method: "POST",
      body,
      schema: PlacementGroup,
      idempotencyKey: crypto.randomUUID(),
    }),
  );

export const useUpdatePlacementGroup = (projectId: string) =>
  useGroupMutation(
    ({ id, ...body }: { id: string; name?: string; labels?: Record<string, string> }) =>
      api(`${base(projectId)}/${id}`, { method: "PATCH", body, schema: PlacementGroup }),
  );

const useGroupAction = (projectId: string, name: "add-server" | "remove-server") =>
  useGroupMutation(({ id, serverId }: { id: string; serverId: string }) =>
    api(`${base(projectId)}/${id}/actions/${name}`, {
      method: "POST",
      body: { serverId },
      schema: PlacementGroupChange,
    }),
  );

export const useAddToPlacementGroup = (projectId: string) =>
  useGroupAction(projectId, "add-server");
export const useRemoveFromPlacementGroup = (projectId: string) =>
  useGroupAction(projectId, "remove-server");

export const useDeletePlacementGroup = (projectId: string) =>
  useGroupMutation((id: string) => api(`${base(projectId)}/${id}`, { method: "DELETE" }));
